import { Box, Stack, Text, Group } from "@mantine/core";
import type { CSSProperties } from "react";
import type { Section } from "@/utils/gradingEngine.types";

const BRAND = "var(--mantine-color-primary-6)";

export type SectionStat = {
  /** Section id */
  id: string;
  /** Display title */
  title: string;
  /** Percentage 0..100 */
  percent: number;
  /** Whether this section is the currently focused one */
  active: boolean;
  /** Whether all questions in this section are answered */
  completed: boolean;
};

type Props = {
  sections: Section[];
  stats: SectionStat[];
  onSelect: (sectionIndex: number) => void;
};

const numberBubbleStyle = (active: boolean, completed: boolean): CSSProperties => ({
  width: 28,
  height: 28,
  minWidth: 28,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 600,
  fontSize: 13,
  border: `1.5px solid ${active || completed ? BRAND : "var(--mantine-color-slate-3)"}`,
  backgroundColor: active ? BRAND : completed ? "var(--mantine-color-primary-1)" : "transparent",
  color: active ? "#fff" : completed ? BRAND : "var(--mantine-color-slate-6)",
  transition: "all 120ms ease",
  flexShrink: 0,
});

export default function AssessmentSectionsNav({ sections, stats, onSelect }: Props) {
  return (
    <Box
      p="md"
      style={{
        backgroundColor: "#ffffff",
        borderRight: "1px solid var(--mantine-color-slate-2)",
        height: "100%",
        overflowY: "auto",
      }}
    >
      <Text fw={600} fz="sm" c="slate.7" mb="md">
        Sections
      </Text>
      <Stack gap="xs">
        {sections.map((section, index) => {
          const stat = stats[index];
          const active = !!stat?.active;
          const completed = !!stat?.completed;
          return (
            <Box
              key={section.id}
              role="button"
              tabIndex={0}
              onClick={() => onSelect(index)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelect(index);
                }
              }}
              style={{
                cursor: "pointer",
                padding: "10px 8px",
                borderRadius: 8,
                backgroundColor: active ? "var(--mantine-color-primary-0)" : "transparent",
                transition: "background-color 120ms ease",
              }}
            >
              <Group gap="sm" align="flex-start" wrap="nowrap">
                <Box style={numberBubbleStyle(active, completed && !active)}>{index + 1}</Box>
                <Box style={{ minWidth: 0, flex: 1 }}>
                  <Text
                    fz="sm"
                    fw={active ? 600 : 500}
                    c={active ? "primary.8" : "slate.7"}
                    lh={1.3}
                    style={{ wordBreak: "break-word" }}
                  >
                    {section.title.replace(/^[A-Z]\.\s*/, "")}
                  </Text>
                  <Text fz={11} c="slate.5" mt={2}>
                    {stat?.percent ?? 0}%
                  </Text>
                </Box>
              </Group>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
